import { logger } from '../../utils/logger.js'
import maplibregl, { addProtocol, removeProtocol } from 'maplibre-gl'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { tleService, CONSTELLATIONS, type ConstellationName, type GpRecord } from './tle-service.js'
import { timelineService, type TimelineState } from '../timeline/timeline-service.js'
import { webglTileRenderer } from '../terrain/webgl-tile-renderer.js'
import { demTileCache } from '../terrain/dem-tile-cache.js'
import { DEM_TILE_SIZE } from '../terrain/dem-config.js'
import { packSatPositions, renderLosTileGPU } from './los-coverage-gpu.js'
import LosCoverageWorker from './los-coverage-worker.ts?worker'
import HorizonWorker from './horizon-worker.ts?worker'

const log = logger.create('sat-los')

const PROTOCOL = 'los'
const GROUP_ID = 'satellite-los'
const DEFAULT_OPACITY = 0.5
const TILE_SIZE = DEM_TILE_SIZE
const PADDED_SIZE = TILE_SIZE * 3
const MIN_ELEVATION_DEG = 10
const PROPAGATION_STEP_MS = 30_000
const DEG2RAD = Math.PI / 180
const HORIZON_CACHE_MAX = 32

const BUFFERS = [
  { sourceId: 'satellite-los-source-a', layerId: 'satellite-los-layer-a' },
  { sourceId: 'satellite-los-source-b', layerId: 'satellite-los-layer-b' },
] as const

function quantizeTime(ms: number): number {
  return Math.floor(ms / PROPAGATION_STEP_MS) * PROPAGATION_STEP_MS
}

class LosCoverageService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _enabledConstellations = new Set<ConstellationName>(CONSTELLATIONS.map(c => c.id))
  private _opacity = DEFAULT_OPACITY
  private _minElevation = MIN_ELEVATION_DEG
  private tleCache = new Map<ConstellationName, GpRecord[]>()
  private worker: Worker | null = null
  private loading = false

  // Propagated satellite data (transferred from worker)
  private ecfData: Float32Array | null = null
  private errData: Int8Array | null = null
  private propSatCount = 0
  private propTimeCount = 0
  private propTimeStartMs = 0
  private propTimeStepMs = 0
  private propMinElevRad = MIN_ELEVATION_DEG * DEG2RAD

  // Cached GPU texture for current timestep
  private cachedTimeIndex = -1
  private cachedSatTexture: WebGLTexture | null = null
  private cachedSatCount = 0
  private cachedSatTexHeight = 0

  // Horizon computation worker
  private horizonWorker: Worker | null = null
  private horizonCallbacks = new Map<
    number,
    (result: { packed: Uint8ClampedArray; width: number; height: number } | null) => void
  >()
  private horizonNextId = 0

  // Cached horizon profile textures per tile (terrain is static)
  private horizonCache = new Map<string, WebGLTexture>()
  private contextGeneration = -1

  // Double-buffer state
  private frontBuffer = 0
  // @ts-expect-error TS6133 - assigned during buffer swap, read in future refresh logic
  private backBufferTimeMs = 0
  private backBufferLoading = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private sourceDataHandler: ((e: any) => void) | null = null

  private propagated = false
  private propagating = false
  private pendingPropagate = false
  private currentTimeMs = 0
  private lastRefreshTime = 0
  private pendingRefreshTimer: ReturnType<typeof setTimeout> | null = null
  // @ts-expect-error TS6133 - reserved for refresh performance tracking
  private refreshStartTime = 0
  private refreshGeneration = 0
  private static readonly REFRESH_INTERVAL_MS = 200

  get enabled(): boolean {
    return this._enabled
  }

  get opacity(): number {
    return this._opacity
  }

  get minElevation(): number {
    return this._minElevation
  }

  get enabledConstellations(): ReadonlySet<ConstellationName> {
    return this._enabledConstellations
  }

  register(map: maplibregl.Map): void {
    this.map = map
    addProtocol(PROTOCOL, (params, abortController) => {
      return this.handleTileRequest(params, abortController)
    })
  }

  unregister(): void {
    this.disable()
    try {
      removeProtocol(PROTOCOL)
    } catch (_) {
      /* may not be registered */
    }
    this.map = null
  }

  async enable(): Promise<void> {
    if (!this.map || this._enabled) return
    this._enabled = true

    try {
      this.worker = new LosCoverageWorker()
    } catch (err) {
      log.error('Worker creation failed:', err)
      this._enabled = false
      return
    }
    this.worker.onmessage = (e: MessageEvent) => this.handleWorkerMessage(e.data)
    this.worker.onerror = err => {
      log.error('Worker error:', err)
      this.propagating = false
    }

    this.horizonWorker = new HorizonWorker()
    this.horizonWorker.onmessage = (e: MessageEvent) => {
      const { id, packed, width, height, error } = e.data
      const cb = this.horizonCallbacks.get(id)
      if (cb) {
        this.horizonCallbacks.delete(id)
        if (error) {
          log.warn(`Horizon worker error (id=${id}):`, error)
          cb(null)
        } else {
          cb({ packed, width, height })
        }
      }
    }
    this.horizonWorker.onerror = err => {
      log.error('Horizon worker crashed:', err)
      for (const cb of this.horizonCallbacks.values()) cb(null)
      this.horizonCallbacks.clear()
    }

    try {
      await this.fetchTles()
    } catch (err) {
      log.error('TLE fetch failed:', err)
    }

    this.currentTimeMs = quantizeTime(timelineService.getState().currentTime.getTime())
    this.frontBuffer = 0
    this.backBufferLoading = false

    this.triggerPropagate()
    timelineService.addListener(this.onTimelineChange)
  }

  disable(): void {
    this._enabled = false
    timelineService.removeListener(this.onTimelineChange)

    if (this.pendingRefreshTimer !== null) {
      clearTimeout(this.pendingRefreshTimer)
      this.pendingRefreshTimer = null
    }

    if (this.sourceDataHandler && this.map) {
      this.map.off('sourcedata', this.sourceDataHandler)
      this.sourceDataHandler = null
    }
    this.removeBuffer(0)
    this.removeBuffer(1)

    if (this.cachedSatTexture) {
      webglTileRenderer.deleteTexture(this.cachedSatTexture)
      this.cachedSatTexture = null
    }
    this.cachedTimeIndex = -1

    for (const tex of this.horizonCache.values()) {
      webglTileRenderer.deleteTexture(tex)
    }
    this.horizonCache.clear()

    this.worker?.terminate()
    this.worker = null
    this.horizonWorker?.terminate()
    this.horizonWorker = null
    this.horizonCallbacks.clear()
    this.ecfData = null
    this.errData = null
    this.propagated = false
    this.propagating = false
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else void this.enable()
  }

  toggleConstellation(id: ConstellationName): void {
    if (this._enabledConstellations.has(id)) {
      this._enabledConstellations.delete(id)
    } else {
      this._enabledConstellations.add(id)
    }
    if (this._enabled) this.triggerPropagate()
  }

  setEnabledConstellations(ids: ConstellationName[]): void {
    this._enabledConstellations = new Set(ids)
    if (this._enabled) this.triggerPropagate()
  }

  setOpacity(value: number): void {
    this._opacity = value
    if (!this.map || !this._enabled) return
    const front = BUFFERS[this.frontBuffer]
    if (this.map.getLayer(front.layerId)) {
      this.map.setPaintProperty(front.layerId, 'raster-opacity', value)
    }
  }

  setMinElevation(deg: number): void {
    this._minElevation = deg
    this.propMinElevRad = deg * DEG2RAD
    if (this._enabled && this.propagated) {
      this.doRefresh()
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) return
    if (this.propagated) {
      this.currentTimeMs = quantizeTime(timelineService.getState().currentTime.getTime())
      this.frontBuffer = 0
      this.backBufferLoading = false
      this.addBuffer(0, this.currentTimeMs, this._opacity)
    }
  }

  // ---- Protocol handler ----

  private invalidateGpuCaches(): void {
    const gen = webglTileRenderer.contextGeneration
    if (gen === this.contextGeneration) return
    this.contextGeneration = gen
    this.cachedSatTexture = null
    this.cachedSatCount = 0
    this.cachedTimeIndex = -1
    this.horizonCache.clear()
    log.info('GPU caches invalidated after context loss')
  }

  // RGBA pixels -> ImageBitmap. MapLibre v5 takes a protocol response whose `data` is an
  // ImageBitmap directly (no image decode). This avoids the BMP container, whose alpha channel
  // Chromium's decoder ignores (rendering semi-transparent tiles as solid black).
  // premultiplyAlpha: 'none' is required: MapLibre uploads with UNPACK_PREMULTIPLY_ALPHA=true,
  // so the source must carry STRAIGHT alpha. (Matches slope-angle / sun-exposure services.)
  private toBitmap(pixels: Uint8ClampedArray): Promise<ImageBitmap> {
    return createImageBitmap(
      new ImageData(pixels as Uint8ClampedArray<ArrayBuffer>, TILE_SIZE, TILE_SIZE),
      { premultiplyAlpha: 'none' }
    )
  }

  private async handleTileRequest(
    params: { url: string },
    abortController: AbortController
  ): Promise<{ data: ImageBitmap }> {
    const match = params.url.match(/(\d+)\/(\d+)\/(\d+)\?t=(\d+)/)
    if (!match) throw new Error(`Invalid LOS tile URL: ${params.url}`)

    const z = parseInt(match[1])
    const x = parseInt(match[2])
    const y = parseInt(match[3])
    const timeMs = parseInt(match[4])
    const signal = abortController.signal

    try {
      return await this.renderLosTile(z, x, y, timeMs, signal)
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e
      log.warn(`tile ${z}/${x}/${y} failed:`, e)
      return { data: await this.getTransparentTile() }
    }
  }

  private async renderLosTile(
    z: number,
    x: number,
    y: number,
    timeMs: number,
    signal: AbortSignal
  ): Promise<{ data: ImageBitmap }> {
    this.invalidateGpuCaches()
    this.ensureSatTexture(timeMs)
    if (!this.cachedSatTexture || this.cachedSatCount === 0) {
      const pixels = new Uint8ClampedArray(TILE_SIZE * TILE_SIZE * 4)
      fillNoVisibility(pixels)
      return { data: await this.toBitmap(pixels) }
    }

    if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

    const horizonTexture = await this.ensureHorizonTexture(z, x, y, signal)
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

    if (!horizonTexture) {
      const pixels = new Uint8ClampedArray(TILE_SIZE * TILE_SIZE * 4)
      fillNoVisibility(pixels)
      return { data: await this.toBitmap(pixels) }
    }

    const pixels = renderLosTileGPU(
      this.cachedSatTexture,
      this.cachedSatCount,
      this.cachedSatTexHeight,
      horizonTexture,
      z,
      x,
      y,
      this.propMinElevRad
    )

    if (!pixels) {
      const fallback = new Uint8ClampedArray(TILE_SIZE * TILE_SIZE * 4)
      fillNoVisibility(fallback)
      return { data: await this.toBitmap(fallback) }
    }

    let hasOcclusion = false
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] > 10) {
        hasOcclusion = true
        break
      }
    }

    if (!hasOcclusion) {
      return { data: await this.getTransparentTile() }
    }

    return { data: await this.toBitmap(pixels) }
  }

  private ensureSatTexture(timeMs: number): void {
    if (!this.ecfData || !this.errData) return

    const ti = Math.max(
      0,
      Math.min(
        this.propTimeCount - 1,
        Math.round((timeMs - this.propTimeStartMs) / this.propTimeStepMs)
      )
    )

    if (ti === this.cachedTimeIndex) return

    if (this.cachedSatTexture) {
      webglTileRenderer.deleteTexture(this.cachedSatTexture)
      this.cachedSatTexture = null
    }

    if (!webglTileRenderer.available && !webglTileRenderer.init()) return

    const { packed, width, height, validCount } = packSatPositions(
      this.ecfData,
      this.errData,
      this.propSatCount,
      this.propTimeCount,
      ti
    )

    if (validCount === 0) {
      this.cachedTimeIndex = ti
      this.cachedSatCount = 0
      return
    }

    this.cachedSatTexture = webglTileRenderer.uploadTexture(packed, width, height)
    this.cachedSatCount = validCount
    this.cachedSatTexHeight = height
    this.cachedTimeIndex = ti
  }

  // ---- Horizon profiles (terrain-based, cached per tile) ----

  private async ensureHorizonTexture(
    z: number,
    x: number,
    y: number,
    signal?: AbortSignal
  ): Promise<WebGLTexture | null> {
    const tileKey = `${z}/${x}/${y}`
    const cached = this.horizonCache.get(tileKey)
    if (cached) return cached

    if (!webglTileRenderer.available && !webglTileRenderer.init()) return null

    const paddedDem = await this.fetchPaddedDem(z, x, y, signal)
    if (!paddedDem) return null
    if (signal?.aborted) return null

    const tileToLat = (ty: number, tz: number): number => {
      const n = Math.PI - (2 * Math.PI * ty) / Math.pow(2, tz)
      return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
    }
    const centerLat = (tileToLat(y, z) + tileToLat(y + 1, z)) / 2
    const metersPerPixel =
      (156543.03 * Math.cos(centerLat * DEG2RAD) * 256) / (Math.pow(2, z) * TILE_SIZE)

    const horizonResult = await this.computeHorizonInWorker(paddedDem, metersPerPixel)
    if (!horizonResult) return null
    if (signal?.aborted) return null

    const texture = webglTileRenderer.uploadTexture(
      horizonResult.packed,
      horizonResult.width,
      horizonResult.height
    )
    if (!texture) return null

    if (this.horizonCache.size >= HORIZON_CACHE_MAX) {
      const firstKey = this.horizonCache.keys().next().value!
      const evicted = this.horizonCache.get(firstKey)!
      webglTileRenderer.deleteTexture(evicted)
      this.horizonCache.delete(firstKey)
    }
    this.horizonCache.set(tileKey, texture)
    return texture
  }

  private computeHorizonInWorker(
    paddedDem: Uint8ClampedArray,
    metersPerPixel: number
  ): Promise<{ packed: Uint8ClampedArray; width: number; height: number } | null> {
    return new Promise(resolve => {
      const id = this.horizonNextId++
      const timeout = setTimeout(() => {
        this.horizonCallbacks.delete(id)
        log.warn(`Horizon computation timed out (id=${id})`)
        resolve(null)
      }, 30_000)
      this.horizonCallbacks.set(id, result => {
        clearTimeout(timeout)
        resolve(result)
      })
      this.horizonWorker!.postMessage(
        { id, paddedDem, metersPerPixel },
        { transfer: [paddedDem.buffer] }
      )
    })
  }

  private async fetchPaddedDem(
    z: number,
    x: number,
    y: number,
    signal?: AbortSignal
  ): Promise<Uint8ClampedArray | null> {
    const maxTile = Math.pow(2, z) - 1
    const padded = new Uint8ClampedArray(PADDED_SIZE * PADDED_SIZE * 4)

    const fetches: Promise<{ dx: number; dy: number; pixels: Uint8ClampedArray | null }>[] = []
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx
        const ny = y + dy
        if (ny < 0 || ny > maxTile) {
          fetches.push(Promise.resolve({ dx, dy, pixels: null }))
        } else {
          const wrappedX = ((nx % (maxTile + 1)) + (maxTile + 1)) % (maxTile + 1)
          fetches.push(
            demTileCache
              .getTile(z, wrappedX, ny, signal)
              .then(pixels => ({ dx, dy, pixels }))
              .catch(() => ({ dx, dy, pixels: null as Uint8ClampedArray | null }))
          )
        }
      }
    }

    const results = await Promise.all(fetches)

    let centerPixels: Uint8ClampedArray | null = null
    for (const { dx, dy, pixels } of results) {
      if (!pixels) continue
      if (dx === 0 && dy === 0) centerPixels = pixels
      const offsetX = (dx + 1) * TILE_SIZE
      const offsetY = (dy + 1) * TILE_SIZE
      for (let row = 0; row < TILE_SIZE; row++) {
        const srcStart = row * TILE_SIZE * 4
        const dstStart = ((offsetY + row) * PADDED_SIZE + offsetX) * 4
        padded.set(pixels.subarray(srcStart, srcStart + TILE_SIZE * 4), dstStart)
      }
    }

    if (!centerPixels) return null

    for (const { dx, dy, pixels } of results) {
      if (pixels) continue
      const offsetX = (dx + 1) * TILE_SIZE
      const offsetY = (dy + 1) * TILE_SIZE
      for (let row = 0; row < TILE_SIZE; row++) {
        const srcRow = Math.max(0, Math.min(TILE_SIZE - 1, row + dy * TILE_SIZE))
        for (let col = 0; col < TILE_SIZE; col++) {
          const srcCol = Math.max(0, Math.min(TILE_SIZE - 1, col + dx * TILE_SIZE))
          const srcIdx = (srcRow * TILE_SIZE + srcCol) * 4
          const dstIdx = ((offsetY + row) * PADDED_SIZE + offsetX + col) * 4
          padded[dstIdx] = centerPixels[srcIdx]
          padded[dstIdx + 1] = centerPixels[srcIdx + 1]
          padded[dstIdx + 2] = centerPixels[srcIdx + 2]
          padded[dstIdx + 3] = centerPixels[srcIdx + 3]
        }
      }
    }

    return padded
  }

  // ---- Double-buffer management ----

  private addBuffer(index: number, timeMs: number, opacity: number): void {
    if (!this.map) return
    const { sourceId, layerId } = BUFFERS[index]

    if (!this.map.getSource(sourceId)) {
      this.map.addSource(sourceId, {
        type: 'raster',
        tiles: [`${PROTOCOL}://{z}/{x}/{y}?t=${timeMs}`],
        tileSize: TILE_SIZE,
        minzoom: 8,
        maxzoom: 12,
        attribution: 'Satellite TLEs: Space-Track',
      })
    }

    if (!this.map.getLayer(layerId)) {
      addOverlayLayer(
        this.map,
        {
          id: layerId,
          type: 'raster',
          source: sourceId,
          minzoom: 8,
          paint: {
            'raster-opacity': opacity,
            'raster-opacity-transition': { duration: 0, delay: 0 },
            'raster-fade-duration': 0,
          },
        },
        GROUP_ID
      )
    }
  }

  private removeBuffer(index: number): void {
    if (!this.map) return
    const { sourceId, layerId } = BUFFERS[index]
    if (this.map.getLayer(layerId)) this.map.removeLayer(layerId)
    if (this.map.getSource(sourceId)) this.map.removeSource(sourceId)
  }

  private trySwap(): void {
    if (!this.backBufferLoading || !this.map || !this._enabled) return

    this.backBufferLoading = false

    const oldFrontIndex = this.frontBuffer
    const backIndex = 1 - oldFrontIndex
    const front = BUFFERS[oldFrontIndex]
    const back = BUFFERS[backIndex]

    if (this.map.getLayer(front.layerId)) {
      this.map.setPaintProperty(front.layerId, 'raster-opacity', 0)
    }
    if (this.map.getLayer(back.layerId)) {
      this.map.setPaintProperty(back.layerId, 'raster-opacity', this._opacity)
    }
    this.frontBuffer = backIndex

    const gen = this.refreshGeneration
    this.map.once('render', () => {
      if (gen !== this.refreshGeneration) return
      this.removeBuffer(oldFrontIndex)
    })
  }

  // ---- Timeline ----

  private onTimelineChange = (_state: Readonly<TimelineState>): void => {
    if (!this._enabled || !this.map || !this.propagated) return

    const quantized = quantizeTime(_state.currentTime.getTime())
    if (quantized === this.currentTimeMs) return

    this.currentTimeMs = quantized
    this.scheduleRefresh()
  }

  private scheduleRefresh(): void {
    const now = performance.now()
    const elapsed = now - this.lastRefreshTime

    if (elapsed >= LosCoverageService.REFRESH_INTERVAL_MS) {
      this.doRefresh()
    } else {
      if (this.pendingRefreshTimer !== null) {
        clearTimeout(this.pendingRefreshTimer)
      }
      const delay = LosCoverageService.REFRESH_INTERVAL_MS - elapsed
      this.pendingRefreshTimer = setTimeout(() => {
        this.pendingRefreshTimer = null
        if (this._enabled && this.map) this.doRefresh()
      }, delay)
    }
  }

  private doRefresh(): void {
    if (!this.map || !this._enabled) return
    this.lastRefreshTime = performance.now()
    this.refreshGeneration++

    if (this.sourceDataHandler) {
      this.map.off('sourcedata', this.sourceDataHandler)
      this.sourceDataHandler = null
    }

    const backIndex = 1 - this.frontBuffer
    this.backBufferTimeMs = this.currentTimeMs

    this.removeBuffer(backIndex)
    this.addBuffer(backIndex, this.currentTimeMs, 0)
    this.backBufferLoading = true

    const sourceId = BUFFERS[backIndex].sourceId
    this.sourceDataHandler = (e: { sourceId: string }) => {
      if (e.sourceId !== sourceId || !this.map) return
      if (!this.map.isSourceLoaded(sourceId)) return
      this.map.off('sourcedata', this.sourceDataHandler!)
      this.sourceDataHandler = null
      this.trySwap()
    }
    this.map.on('sourcedata', this.sourceDataHandler)
  }

  // ---- Propagation ----

  private triggerPropagate(): void {
    if (!this.worker) return

    if (this.propagating) {
      this.pendingPropagate = true
      return
    }

    const records: GpRecord[] = []
    for (const c of CONSTELLATIONS) {
      if (!this._enabledConstellations.has(c.id)) continue
      records.push(...(this.tleCache.get(c.id) ?? []))
    }

    if (records.length === 0) {
      this.propagated = false
      this.removeBuffer(0)
      this.removeBuffer(1)
      return
    }

    this.propagating = true
    this.propagated = false

    const state = timelineService.getState()
    const startMs = state.startTime.getTime()
    const endMs = state.endTime.getTime()
    const timeCount = Math.ceil((endMs - startMs) / PROPAGATION_STEP_MS) + 1

    log.info(`propagating ${records.length} sats, ${timeCount} steps`)

    this.worker.postMessage({
      type: 'propagate',
      gpRecords: records,
      timeStartMs: startMs,
      timeStepMs: PROPAGATION_STEP_MS,
      timeCount,
      minElevationDeg: this._minElevation,
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleWorkerMessage(data: Record<string, any>): void {
    if (data.type === 'propagated') {
      this.propagating = false
      this.propagated = true

      this.ecfData = data.ecfData as Float32Array
      this.errData = data.errData as Int8Array
      this.propSatCount = data.satCount as number
      this.propTimeCount = data.timeCount as number
      this.propTimeStartMs = data.timeStartMs as number
      this.propTimeStepMs = data.timeStepMs as number
      this.propMinElevRad = data.minElevationRad as number

      // Invalidate cached texture
      if (this.cachedSatTexture) {
        webglTileRenderer.deleteTexture(this.cachedSatTexture)
        this.cachedSatTexture = null
      }
      this.cachedTimeIndex = -1

      log.info(
        `propagated ${this.propSatCount} sats, ${this.propTimeCount} steps, ${((this.ecfData.byteLength + this.errData.byteLength) / 1024 / 1024).toFixed(1)}MB`
      )

      if (this.pendingPropagate) {
        this.pendingPropagate = false
        this.triggerPropagate()
      } else if (this.map && this._enabled) {
        this.currentTimeMs = quantizeTime(timelineService.getState().currentTime.getTime())
        this.addBuffer(0, this.currentTimeMs, this._opacity)
      }
    }
  }

  // ---- Helpers ----

  // A fully-transparent tile (alpha 0 everywhere). Returns a fresh ImageBitmap per call rather
  // than caching one instance, since MapLibre consumes the bitmap on GPU upload.
  private getTransparentTile(): Promise<ImageBitmap> {
    return this.toBitmap(new Uint8ClampedArray(TILE_SIZE * TILE_SIZE * 4))
  }

  private async fetchTles(): Promise<void> {
    if (this.loading) return
    this.loading = true
    try {
      const fetches = CONSTELLATIONS.map(async c => {
        const data = await tleService.getConstellation(c.id)
        this.tleCache.set(c.id, data)
      })
      await Promise.all(fetches)
    } finally {
      this.loading = false
    }
  }
}

function fillNoVisibility(pixels: Uint8ClampedArray): void {
  for (let i = 0; i < pixels.length; i += 4) {
    pixels[i] = 220
    pixels[i + 1] = 40
    pixels[i + 2] = 40
    pixels[i + 3] = 140
  }
}

export const losCoverageService = new LosCoverageService()
